// src/pages/Users/Messaging/CreateResponseMessageForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { sendMessage } from "../../../services/messageServices";
import { getUserById } from "../../../services/userServices";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { IoIosSend } from "react-icons/io";
import { FaTimes } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { toAbsoluteUserPhotoUrl } from "../../../services/userPhotoServices";


/* Quill sobre */
const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};
const quillFormats = ["header", "bold", "italic", "underline", "list", "bullet", "link", "clean"];

/* Rôle → label + ton */
function roleMeta(role_id) {
  if (role_id === 1) return { label: "Administrateur", tone: "red" };
  if (role_id === 3) return { label: "Enseignant", tone: "green" };
  return { label: "Utilisateur", tone: "slate" };
}
function roleBadgeClass(tone) {
  switch (tone) {
    case "red":
      return "bg-red-600 text-white";
    case "green":
      return "bg-emerald-600 text-white";
    default:
      return "bg-slate-600 text-white";
  }
}

export default function CreateResponseMessageForm({
  onSuccess,
  show,
  onHide,
  userId,
  initialRecipientId,
  initialSubject,
}) {
  const [messageData, setMessageData] = useState({
    recipient_id: initialRecipientId || "",
    subject: initialSubject || "",
    content: "",
  });

  const [recipient, setRecipient] = useState(null); // { id, nom, prenom, role_id, photoURL }
  const [loadingRecipient, setLoadingRecipient] = useState(true);
  const [sending, setSending] = useState(false);

  // Re-init si on rouvre / change props
  useEffect(() => {
    setMessageData({
      recipient_id: initialRecipientId || "",
      subject: initialSubject || "",
      content: "",
    });
  }, [initialRecipientId, initialSubject, show]);

  // Fetch destinataire (nom, avatar, rôle)
  useEffect(() => {
    if (!show || !initialRecipientId) return;
    let cancelled = false;

    const fetchRecipient = async () => {
      setLoadingRecipient(true);
      try {
        const u = await getUserById(initialRecipientId);
        if (!cancelled) {
          setRecipient(u || null);
        }
      } catch (e) {
        console.error("Failed to fetch user details:", e);
        if (!cancelled) setRecipient(null);
      } finally {
        if (!cancelled) setLoadingRecipient(false);
      }
    };

    fetchRecipient();
    return () => {
      cancelled = true;
    };
  }, [show, initialRecipientId]);

  const fullName = useMemo(() => {
    if (!recipient) return "";
    return `${recipient.nom} ${recipient.prenom}`;
  }, [recipient]);

  // Avatar (local ou Cloudinary)
  const avatar = toAbsoluteUserPhotoUrl(recipient?.photoURL || recipient?.photo_url || "/img/user_avatar.jpg");
  const { label: roleLabel, tone: roleTone } = roleMeta(recipient?.role_id);

  const handleSubjectChange = (e) => {
    setMessageData((p) => ({ ...p, subject: e.target.value }));
  };

  const handleContentChange = (val) => {
    setMessageData((p) => ({ ...p, content: val }));
  };

  const canSubmit = !!messageData.recipient_id && !!messageData.subject && !!messageData.content;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Merci de remplir tous les champs.");
      return;
    }
    try {
      setSending(true);
      await sendMessage({
        sender_id: userId,
        recipient_id: messageData.recipient_id,
        subject: (messageData.subject || "").trim(),
        content: messageData.content,
      });
      toast.success("Message envoyé avec succès.");
      onSuccess?.();
      onHide?.();
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Erreur lors de l'envoi du message.");
    } finally {
      setSending(false);
    }
  };

  if (!show) return null;

  return (
    <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/70 backdrop-blur">
      {/* Header compact */}
      <div className="px-4 py-3 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {loadingRecipient ? (
              <div className="w-8 h-8 rounded-full bg-white/30 animate-pulse" />
            ) : (
              <img
                src={avatar}
                alt={fullName}
                className="w-8 h-8 rounded-full object-cover ring-1 ring-white/40"
              />
            )}
            <div className="min-w-0">
              <p className="text-sm sm:text-base font-semibold truncate">
                {loadingRecipient ? "…" : fullName}
              </p>
              {!loadingRecipient && (
                <span
                  className={`inline-flex items-center rounded-full px-2 py-[2px] text-[10px] font-semibold ${roleBadgeClass(
                    roleTone
                  )}`}
                >
                  {roleLabel}
                </span>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={onHide}
            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs bg-white/15 hover:bg-white/25 transition"
            title="Fermer"
          >
            <FaTimes className="text-[12px]" />
            <span className="hidden sm:inline">Fermer</span>
          </button>
        </div>
      </div>

      {/* Corps */}
      <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-4">
        {/* Destinataire (readonly) + sujet */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr] gap-3">
          {/* <div>
            <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
              Destinataire
            </label>
            <input
              type="text"
              value={
                loadingRecipient
                  ? `Chargement… (ID: ${initialRecipientId})`
                  : fullName || `Inconnu (ID: ${initialRecipientId})`
              }
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/70 px-3 py-2 text-slate-900 dark:text-slate-100"
              readOnly
            />
          </div> */}

          <div>
            <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
              Sujet
            </label>
            <input
              type="text"
              value={messageData.subject}
              onChange={handleSubjectChange}
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
              required
            />
          </div>
        </div>

        {/* Contenu */}
        <div>
          <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
            Contenu du message
          </label>
          <div className="rounded-xl overflow-hidden ring-1 ring-slate-300 dark:ring-slate-700">
            <ReactQuill
              theme="snow"
              value={messageData.content}
              onChange={handleContentChange}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Rédigez votre réponse…"
              className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
            />
          </div>
        </div>

        {/* Actions compactes */}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onHide}
            className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm ring-1 ring-slate-300 dark:ring-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            title="Annuler"
          >
            <FaTimes className="text-[12px]" />
            Annuler
          </button>
          <Button
            type="submit"
            text={sending ? "Envoi…" : "Envoyer"}
            icon={IoIosSend}
            className="gap-2 !rounded-lg"
            disabled={sending || loadingRecipient || !canSubmit}
          />
        </div>
      </form>
    </div>
  );
}
